import { useLocation, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { AppShell } from "@/app/AppShell";
import { useAuth } from "@/lib/auth/auth-context";

/**
 * Guard for every authenticated route. While /auth/me is resolving (including
 * the silent token refresh on reload) it shows a spinner; if there's no valid
 * session it redirects to /login, preserving the attempted path so the user
 * lands back there after signing in (the 401→login behaviour). Authenticated
 * users get the app shell with the routed page in its <Outlet />.
 */
export function AuthedLayout() {
  const { status } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Freeze the attempted path at first render so it survives the redirect
  // transition (location flips to /login while this guard is still mounted).
  const [attemptedPath] = useState(() => location.pathname);

  // Redirect via a one-shot effect rather than a render-time <Navigate>: the
  // guard stays mounted during the pending transition, so a <Navigate> would
  // re-fire every re-render and loop-commit the router ("Maximum update depth
  // exceeded"). The effect's deps are stable, so it navigates exactly once.
  useEffect(() => {
    if (status === "unauthenticated") {
      void navigate({
        to: "/login",
        search: { redirect: attemptedPath },
        replace: true,
      });
    }
  }, [status, navigate, attemptedPath]);

  // Show the spinner while loading and while the redirect is in flight.
  if (status !== "authenticated") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="size-8 animate-spin text-muted-foreground" aria-label="Loading" />
      </div>
    );
  }

  return <AppShell />;
}
